import { CommonDialog } from "@/components/common/CommonDialog";
import { FormButtonGroup } from "@/components/form";
import {
  Typography,
  Stack,
  Box,
  Divider,
  Avatar,
  Link,
  Grid,
  CircularProgress,
} from "@mui/material";
import {
  Info,
  WhatsApp,
  Person,
  Phone,
  Warning,
} from "@mui/icons-material";
import { useSessionContext } from "@/contexts/SessionContext";
import MessageTemplatePhonePreview from "src-next/modules/crm-clinical/messageTemplates/components/MessageTemplatePhonePreview";
import { resolveExamDescriptionText } from "../../helpers/buildPostAttendanceFollowUpPreview";
import { usePostAttendanceFollowUpPreview } from "../../hooks/usePostAttendanceFollowUpPreview";
import type { PostAttendanceFollowUpType } from "../../hooks/usePostAttendanceFollowUpSendDialog";

interface FollowUpModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  data: any;
  type: PostAttendanceFollowUpType;
  onSendFollowUp: () => void;
  loading?: boolean;
  patientData?: { id: string; name: string; phone?: string; cpf?: string };
}

/**
 * Modal de confirmação do pós-atendimento com prévia do template WhatsApp
 * (`flow_medication` ou `flow_exame`) antes de abrir o envio.
 */
export function FollowUpModal({
  open,
  onClose,
  title,
  data,
  type,
  onSendFollowUp,
  loading,
  patientData,
}: FollowUpModalProps) {
  const { access } = useSessionContext();
  const { preview, isLoading: isPreviewLoading } = usePostAttendanceFollowUpPreview(
    {
      open,
      type,
      data,
      patientData,
    },
  );

  const examDescription = resolveExamDescriptionText(data);

  const getFeedbackUrl = () => {
    return access.type === "professional"
      ? "https://projectcore.atlassian.net/servicedesk/customer/portal/11"
      : "https://projectcore.atlassian.net/servicedesk/customer/portal/12";
  };

  return (
    <CommonDialog isOpen={open} handleClose={onClose} maxWidth="md">
      <Stack spacing={2} sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar sx={{ bgcolor: "primary.main" }}>
            <WhatsApp />
          </Avatar>
          <Typography variant="h6" sx={{ color: "neutral1000.main" }}>
            {title}
          </Typography>
        </Stack>

        <Divider />

        <Box
          sx={{
            backgroundColor: "primary.lighter",
            borderRadius: 2,
            p: 2,
          }}
        >
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            Dados do Paciente
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Stack spacing={1}>
                <Typography variant="subtitle2" color="text.secondary">
                  Nome
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Person color="primary" fontSize="small" />
                  <Typography>
                    {patientData?.name || "Nome não informado"}
                  </Typography>
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Stack spacing={1}>
                <Typography variant="subtitle2" color="text.secondary">
                  Telefone
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Phone color="primary" fontSize="small" />
                  <Typography>
                    {patientData?.phone || "Telefone não informado"}
                  </Typography>
                </Stack>
              </Stack>
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={{
            backgroundColor: "neutral.lighter",
            borderRadius: 2,
            p: 2,
          }}
        >
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            Detalhes do {type === "medication" ? "Medicamento" : "Exame"}
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Stack spacing={1}>
                <Typography variant="subtitle2" color="text.secondary">
                  {type === "medication" ? "Medicamento" : "Exame"}
                </Typography>
                <Typography>
                  {type === "medication" ? data?.description : examDescription}
                </Typography>
              </Stack>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Stack spacing={1}>
                <Typography variant="subtitle2" color="text.secondary">
                  Profissional
                </Typography>
                <Typography>{data?.practitioner}</Typography>
              </Stack>
            </Grid>

            {type === "medication" && (
              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Posologia
                  </Typography>
                  <Typography>{data?.dose}</Typography>
                </Stack>
              </Grid>
            )}

            {type === "exam" && data?.serviceRequest && (
              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Detalhes do Exame
                  </Typography>
                  <Typography>
                    {data?.serviceRequest?.code?.concept?.coding?.[0]?.display}
                  </Typography>
                  {data?.serviceRequest?.note?.[0]?.text && (
                    <Typography variant="body2" color="text.secondary">
                      Observação: {data?.serviceRequest?.note?.[0]?.text}
                    </Typography>
                  )}
                </Stack>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <Stack spacing={1}>
                <Typography variant="subtitle2" color="text.secondary">
                  Data do atendimento
                </Typography>
                <Typography>{data?.date}</Typography>
              </Stack>
            </Grid>
          </Grid>
        </Box>

        <Stack
          spacing={2}
          alignItems="left"
          sx={{
            backgroundColor: "background.paper",
            p: 2,
            borderRadius: 1,
            border: "1px dashed",
            borderColor: "divider",
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            justifyContent="left"
            alignItems="center"
          >
            <Warning sx={{ color: "text.secondary" }} />
            <Typography variant="body1" color="text.secondary">
              Esta é uma mensagem pré-programada e não pode ser alterada
            </Typography>
          </Stack>

          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Prévia da Mensagem
          </Typography>

          {isPreviewLoading ? (
            <Stack alignItems="center" py={2}>
              <CircularProgress size={28} />
            </Stack>
          ) : (
            <MessageTemplatePhonePreview
              header={preview.header}
              body={preview.body}
              hasButton={preview.hasButton}
              buttonText={preview.buttonText}
              variant="default"
            />
          )}

          <Divider />

          <Stack direction="row" spacing={1} alignItems="center">
            <Info sx={{ color: "text.secondary", fontSize: "0.875rem" }} />
            <Typography variant="body1" color="text.secondary">
              Tem sugestões para melhorar esta mensagem?{" "}
              <Link
                href={getFeedbackUrl()}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
              >
                Envie seu feedback
              </Link>
            </Typography>
          </Stack>
        </Stack>

        <FormButtonGroup
          onGoBackButtonClick={onClose}
          onNextButtonClick={onSendFollowUp}
          nextButtonText="Enviar Acompanhamento"
          goBackButtonText="Cancelar"
          loading={loading || isPreviewLoading}
        />
      </Stack>
    </CommonDialog>
  );
}
